import { isShellRuntime } from './shellDetection';

/**
 * F-0510 IU-10 (`docs/concept-iphone-ux.md`, QA 0520): the create sheet focuses its title, and in the
 * iPhone and iPad app no keyboard rose with it.
 *
 * WebKit raises the keyboard for a focus only while the page is processing a user gesture. The tap
 * that opens the sheet is one, but the sheet's page is a lazy route: it mounts after a network fetch,
 * the gesture has ended, and its `focus()` is silently ignored (the repo's own Maestro flow 32 tapped
 * the title for that reason). A focus that moves from one field to another while the keyboard is up
 * needs no gesture. So, in the tap, the moment the route changes to a create address, a one-pixel
 * proxy field takes focus (the keyboard rises, inside the gesture); the sheet's title then takes it
 * from the proxy, which goes. A proxy nobody takes the focus from goes after `PROXY_LIFETIME_MS`, so
 * a keyboard never stays up over nothing.
 *
 * The route change is seen where it is made: `history.pushState`, which the router calls inside the
 * tap's handler. Only in the app (`isShellRuntime`), and only while the page's user activation is
 * live (`navigator.userActivation.isActive`): a route restored by Back or a link followed by script
 * is not a gesture and primes nothing.
 */

/** `…/items/new`, `…/decisions/new`, `…/releases/new`: the addresses whose page focuses its title. */
export const CREATE_ROUTE = /\/(?:items|decisions|releases)\/new\/?(?:[?#].*)?$/;

/** How long a proxy waits for the sheet's own field to take the focus. */
export const PROXY_LIFETIME_MS = 2500;

export const PROXY_ATTRIBUTE = 'data-create-keyboard-proxy';

export function isCreateAddress(url: string | URL | null | undefined): boolean {
  if (url === null || url === undefined) return false;
  const text = typeof url === 'string' ? url : url.pathname + url.search + url.hash;
  return CREATE_ROUTE.test(text);
}

function gestureIsLive(): boolean {
  return (
    (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation
      ?.isActive === true
  );
}

let proxy: HTMLInputElement | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

function release(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  proxy?.remove();
  proxy = null;
}

function prime(): void {
  if (proxy || !gestureIsLive()) return;
  const input = document.createElement('input');
  input.type = 'text';
  input.setAttribute(PROXY_ATTRIBUTE, '');
  input.setAttribute('aria-hidden', 'true');
  input.tabIndex = -1;
  input.autocomplete = 'off';
  // 16 px: iOS zooms a smaller field on focus. Fixed and clear, so nothing moves and nothing shows.
  input.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0;font-size:16px;pointer-events:none;';
  document.body.appendChild(input);
  input.focus({ preventScroll: true });
  proxy = input;
  timer = setTimeout(release, PROXY_LIFETIME_MS);
}

/** The field that takes the focus from the proxy ends it. */
function onFocusIn(event: FocusEvent): void {
  if (proxy && event.target !== proxy) release();
}

/** Installs the priming in the app; returns its undoing. In a browser it does nothing. */
export function installCreateKeyboardPrime(): () => void {
  if (!isShellRuntime()) return () => undefined;
  const original = window.history.pushState;
  window.history.pushState = function (this: History, ...args: Parameters<History['pushState']>) {
    const result = original.apply(this, args);
    if (isCreateAddress(args[2] as string | URL | null | undefined)) prime();
    return result;
  };
  document.addEventListener('focusin', onFocusIn);
  return () => {
    window.history.pushState = original;
    document.removeEventListener('focusin', onFocusIn);
    release();
  };
}
